import type { MockRequestContext, MockResponseFn } from '~/mocks/mockApis';

/** request body에 limit/offset 미전달 시 기본 페이지 크기 */
const DEFAULT_PAGE_SIZE = 50;

/**
 * 항목 factory로 count만큼 생성. index는 1부터 시작.
 */
export const makeListItems = <T>(count: number, makeItem: (index: number) => T): T[] =>
  Array.from({ length: count }, (_, i) => makeItem(i + 1));

/**
 * 리스트 응답 envelope 골격. `{ data: { [itemKey]: T[] }, pagination }` 형태를 가정.
 */
type ListEnvelope<TKey extends string, TItem, TPagination> = {
  data: { [K in TKey]: TItem[] };
  pagination: TPagination;
};

/** 기본 추출기: request body에서 `{limit, offset}` 읽기 */
const extractDefaultRange = (body: unknown): { limit: number; offset: number } => {
  const { limit = DEFAULT_PAGE_SIZE, offset = 0 } = (body ?? {}) as { limit?: number; offset?: number };
  return { limit, offset };
};

/**
 * 정적 리스트 응답 빌더.
 *
 * @example
 * const buildListResponse = (count: number): TypePostRequestsRequestedResponse =>
 *   makeListResponse(count, makeRequest, 'receivingRequests', {
 *     listView: 50,
 *     listTotal: count,
 *     cursor: 0,
 *   });
 */
export const makeListResponse = <TKey extends string, TItem, TPagination>(
  count: number,
  makeItem: (index: number) => TItem,
  itemKey: TKey,
  pagination: TPagination
): ListEnvelope<TKey, TItem, TPagination> => ({
  data: { [itemKey]: makeListItems(count, makeItem) } as { [K in TKey]: TItem[] },
  pagination,
});

/**
 * 서버사이드 페이지네이션 시뮬레이션.
 * 기본 동작: request body의 `{limit, offset}`을 읽어 항목을 슬라이스. 미전달 시 limit=DEFAULT_PAGE_SIZE, offset=0.
 * `{page, pageSize}` 등 다른 모양을 쓰는 API는 `extractSliceRange`로 변환 로직을 주입.
 *
 * @example
 * // 기본 (body.limit, body.offset)
 * makePaginatedResponse(250, makeRequest, 'receivingRequests', ({ limit, offset, total }) => ({
 *   listView: limit,
 *   listTotal: total,
 *   cursor: offset,
 * }));
 *
 * @example
 * // 커스텀 추출기 (body.page, body.pageSize)
 * makePaginatedResponse(
 *   250,
 *   makeItem,
 *   'items',
 *   ({ limit, offset, total }) => ({ currentPage: offset / limit, pageSize: limit, totalCount: total }),
 *   ({ body }) => {
 *     const { page = 0, pageSize = 20 } = (body ?? {}) as { page?: number; pageSize?: number };
 *     return { limit: pageSize, offset: page * pageSize };
 *   }
 * );
 */
export const makePaginatedResponse = <TKey extends string, TItem, TPagination>(
  total: number,
  makeItem: (index: number) => TItem,
  itemKey: TKey,
  buildPagination: (ctx: { limit: number; offset: number; total: number }) => TPagination,
  extractSliceRange?: (ctx: MockRequestContext) => { limit: number; offset: number }
): MockResponseFn<ListEnvelope<TKey, TItem, TPagination>> => {
  const all = makeListItems(total, makeItem);
  return (ctx) => {
    const { limit, offset } = extractSliceRange ? extractSliceRange(ctx) : extractDefaultRange(ctx.body);
    return {
      data: { [itemKey]: all.slice(offset, offset + limit) } as { [K in TKey]: TItem[] },
      pagination: buildPagination({ limit, offset, total }),
    };
  };
};
